import { CATEGORY_OPTIONS, CATEGORY_OPTIONS_WITH_COMMON } from './common/categories'
import { createCategoryLandingDefaultFormFields } from './common/formFields'
import { getGenericCodeTemplatePreset } from './common/genericPresets'
import {
  applyCategoryLandingLayout,
  getCategoryLandingLayoutEditableFields,
  getCategoryLandingLayoutOptions,
  normalizeCategoryLandingLayoutConfig,
} from './layouts/registry'
import { getProductAutoPreset } from './PRODUCT_AUTO/presets'
import { getProductCarePreset } from './PRODUCT_CARE/presets'
import { getProductComprehensivePreset } from './PRODUCT_COMPREHENSIVE/presets'
import { getCoverageAnalysisPreset } from './COVERAGE_ANALYSIS/presets'
import { getHiddenInsuranceMoneyPreset } from './HIDDEN_INSURANCE_MONEY/presets'
import { getInsuranceRemodelingPreset } from './INSURANCE_REMODELING/presets'
import { getProductPensionPreset } from './PRODUCT_PENSION/presets'
import { getProductFirePreset } from './PRODUCT_FIRE/presets'
import { getProductWholeLifePreset } from './PRODUCT_WHOLE_LIFE/presets'
import { getPersonalIntroPreset } from './PERSONAL_INTRO/presets'

const GENERIC_CATEGORY_PRESET_RESOLVERS = {
  HIDDEN_INSURANCE_MONEY: getHiddenInsuranceMoneyPreset,
  COVERAGE_ANALYSIS: getCoverageAnalysisPreset,
  INSURANCE_REMODELING: getInsuranceRemodelingPreset,
}

const PRODUCT_CATEGORY_PRESET_RESOLVERS = {
  PRODUCT_AUTO: getProductAutoPreset,
  PRODUCT_COMPREHENSIVE: getProductComprehensivePreset,
  PRODUCT_PENSION: getProductPensionPreset,
  PRODUCT_WHOLE_LIFE: getProductWholeLifePreset,
  PRODUCT_CARE: getProductCarePreset,
  PRODUCT_FIRE: getProductFirePreset,
}

const DIRECT_CATEGORY_PRESET_RESOLVERS = {
  PERSONAL_INTRO: getPersonalIntroPreset,
}

// 문구/섹션 원본 위치를 카테고리 그룹별로 분리해 둔다.
// - 공통형 카테고리: 각 presets.js + common/genericPresets.js
// - 상품군 카테고리: 각 presets.js + common/productPresetFactory.js
// - 본인소개: PERSONAL_INTRO/presets.js 단독
const CODE_TEMPLATE_PRESET_RESOLVERS = {
  ...GENERIC_CATEGORY_PRESET_RESOLVERS,
  ...PRODUCT_CATEGORY_PRESET_RESOLVERS,
  ...DIRECT_CATEGORY_PRESET_RESOLVERS,
}

const CODE_TEMPLATE_PRESET_LABELS = {
  default: '1 - 일반형',
  'clean-form': '2 - 간편 문의형',
  'hanwha-sections': '3 - 설명형',
  'basic-template-4': '4 - CTA 강조형',
  'basic-template-5': '5 - 프리미엄 상담형',
  'lead-gen-fullbleed': '6 - 풀블리드 전환형',
}

const CODE_TEMPLATE_PRESET_HINTS = {
  default: {
    title: '가장 무난한 일반형',
    description: '처음 시작하는 사용자에게 적합한 기본형입니다. 신뢰감 있는 첫 화면과 균형 잡힌 정보 구성이 필요할 때 추천합니다.',
  },
  'clean-form': {
    title: '간편 문의 전환형',
    description: '문구는 짧게 유지하되 상담 기준과 신청 폼을 명확히 보여주는 구조입니다. 광고 유입이나 문의 전환이 중요한 카테고리에 적합합니다.',
  },
  'hanwha-sections': {
    title: '설명형 섹션 확장형',
    description: '히어로 이후에 특징, 절차, 후기 같은 섹션을 충분히 쌓아 설명하고 싶을 때 적합합니다. 상품 이해와 신뢰 형성에 유리합니다.',
  },
  'basic-template-4': {
    title: 'CTA 집중 상단 강조형',
    description: '첫 화면에서 핵심 메시지와 행동 유도를 강하게 주고 싶을 때 추천합니다. 짧은 문구와 명확한 신청 유도에 강합니다.',
  },
  'basic-template-5': {
    title: '프리미엄 상담형',
    description: '조금 더 고급스럽고 전략적인 인상을 주는 템플릿입니다. 1:1 상담, 전문성, 신뢰 중심 브랜딩에 적합합니다.',
  },
  'lead-gen-fullbleed': {
    title: '풀블리드 광고 랜딩형',
    description:
      '한화 키워드 LP와 비슷한 골격입니다: 맨 위 풀폭 이미지 → (선택) 2단 띠 이미지 → PC에서는 우측 고정 문의 폼. 카테고리별로 레이아웃 목록에 「6 - 풀블리드 (○○)」가 따로 표시되며, 적용 시 해당 카테고리 전용 templateStyle 로 저장됩니다.',
  },
}

export {
  CATEGORY_OPTIONS,
  CATEGORY_OPTIONS_WITH_COMMON,
  createCategoryLandingDefaultFormFields,
  getCategoryLandingLayoutOptions,
  getCategoryLandingLayoutEditableFields,
  normalizeCategoryLandingLayoutConfig,
  applyCategoryLandingLayout,
}

export { isLeadGenFullbleedLayoutKey, leadGenFullbleedTemplateStyleForCategory } from './layouts/registry'

export function getCodeTemplatePreset(category, presetKey = 'default') {
  if (!category) return null
  const resolver = CODE_TEMPLATE_PRESET_RESOLVERS[category]
  if (resolver) return resolver(presetKey)
  return getGenericCodeTemplatePreset(category, presetKey)
}

function getCodeTemplatePresetOptions(category) {
  const supportedKeys = category === 'PERSONAL_INTRO'
    ? ['default', 'basic-template-4', 'basic-template-5', 'lead-gen-fullbleed']
    : ['default', 'clean-form', 'hanwha-sections', 'basic-template-4', 'basic-template-5', 'lead-gen-fullbleed']
  return supportedKeys.map((key) => ({
    key,
    label: CODE_TEMPLATE_PRESET_LABELS[key] || key,
    hintTitle: CODE_TEMPLATE_PRESET_HINTS[key]?.title || '',
    hintDescription: CODE_TEMPLATE_PRESET_HINTS[key]?.description || '',
    preset: getCodeTemplatePreset(category, key),
  })).filter((item) => !!item.preset)
}

export function getCodeTemplateApplyOptions(category) {
  return getCodeTemplatePresetOptions(category).map((item) => ({
    value: item.key === 'default' ? 'default' : `preset:${item.key}`,
    label: item.label,
    presetKey: item.key,
    hintTitle: item.hintTitle,
    hintDescription: item.hintDescription,
  }))
}

export function getCodeTemplatePresetHint(presetKey) {
  return CODE_TEMPLATE_PRESET_HINTS[presetKey] || null
}
